import React, { memo, useMemo } from 'react'
import Avatar from 'transfix-ui/components/Avatar'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useUserDetails } from 'utils/hooks'
import { getTruncatedString } from './utils/getTruncatedString'

const useProfileItemStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: brandColors.coolGray0,
    padding: theme.spacing(3, 2),
  },
  avatarWrapper: {
    flex: '0 0 auto',
    marginRight: theme.spacing(1),
  },
  nameWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  name: {
    color: brandColors.coolGray8,
    lineHeight: 1,
    marginBottom: theme.spacing(0.35),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  email: {
    color: brandColors.coolGray8,
    lineHeight: 1,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}))

export interface IProfileItemProps {
  /**
   * @example 'Jane Marie'
   */
  fullName?: string
  /**
   * @example 'jane@aol.com'
   */
  email?: string
}

const ProfileItem = memo<IProfileItemProps>(() => {
  const { userDetails } = useUserDetails()
  const { email, firstName, lastName } = userDetails || {}
  const fullName = useMemo(() => [firstName, lastName].join(' '), [firstName, lastName])
  const fullNameTruncated = useMemo(() => getTruncatedString(fullName), [fullName])
  const emailTruncated = useMemo(() => getTruncatedString(email), [email])
  const classes = useProfileItemStyles()
  return (
    <div className={classes.root} data-test='ProfileItem'>
      <div className={classes.avatarWrapper}>
        <Avatar size='medium' name={fullName} hideTooltip />
      </div>
      <div className={classes.nameWrapper}>
        <Typography variant='button' className={classes.name} component='div'>
          {fullNameTruncated}
        </Typography>
        <Typography variant='body2' className={classes.email} component='div'>
          {emailTruncated}
        </Typography>
      </div>
    </div>
  )
})

export default ProfileItem
